/* Optional Norway estimate. Uses Shopify's configured rates; never blocks checkout. */
class GeethasShippingEstimate extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.input = this.querySelector('[data-postcode]');
    this.button = this.querySelector('[data-estimate]');
    this.result = this.querySelector('[data-result]');
    if (!this.input || !this.button || !this.result) return;
    this.button.addEventListener('click', () => this.estimate(), { signal: this.abort.signal });
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.estimate();
      }
    }, { signal: this.abort.signal });
    this.input.addEventListener('input', () => {
      this.request?.abort();
      this.input.removeAttribute('aria-invalid');
      this.result.replaceChildren();
    }, { signal: this.abort.signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.request?.abort();
    this.abort = null;
  }

  async estimate() {
    if (this.button.disabled) return;
    const postcode = this.input.value.trim();
    if (!/^\d{4}$/.test(postcode)) {
      this.input.setAttribute('aria-invalid', 'true');
      this.result.textContent = this.dataset.invalid;
      this.input.focus();
      return;
    }
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    const timeout = setTimeout(() => request.abort(), 12000);
    this.button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.result.textContent = this.dataset.loading;
    const root = (window.Shopify?.routes?.root || this.dataset.root || '/').replace(/\/?$/, '/');
    const query = new URLSearchParams({
      'shipping_address[zip]': postcode,
      'shipping_address[country]': 'Norway',
    });
    try {
      const prepared = await fetch(`${root}cart/prepare_shipping_rates.json?${query}`, {
        method: 'POST', credentials: 'same-origin', signal: request.signal,
      });
      if (!prepared.ok) throw new Error('shipping');
      const cartResponse = await fetch(`${root}cart.js`, {
        credentials: 'same-origin', cache: 'no-store', signal: request.signal,
      });
      if (!cartResponse.ok) throw new Error('currency');
      const currentCart = await cartResponse.json();
      if (currentCart.currency !== this.dataset.currency ||
          Number(currentCart.total_price) !== Number(this.dataset.total)) throw new Error('currency');
      let rates = null;
      for (let attempt = 0; attempt < 10; attempt += 1) {
        const response = await fetch(`${root}cart/async_shipping_rates.json?${query}`, {
          credentials: 'same-origin', cache: 'no-store', signal: request.signal,
        });
        if (!response.ok) throw new Error('shipping');
        const data = await response.json();
        if (Array.isArray(data?.shipping_rates)) {
          rates = data.shipping_rates;
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 600));
        if (request.signal.aborted) throw new Error('cancelled');
      }
      if (!rates) throw new Error('shipping');
      if (request.signal.aborted || this.input.value.trim() !== postcode) return;
      this.result.replaceChildren();
      if (!rates.length) {
        this.result.textContent = this.dataset.empty;
        return;
      }
      const list = document.createElement('ul');
      for (const rate of rates) {
        const price = Number(rate.price);
        const currency = rate.currency || currentCart.currency;
        // Shopify can omit the rate currency. Confirm the cart currency and total first.
        if (rate.price == null || rate.price === '' || !Number.isFinite(price) || price < 0 || currency !== this.dataset.currency) {
          throw new Error('currency');
        }
        const line = document.createElement('li');
        const formatted = price === 0 ? this.dataset.free : new Intl.NumberFormat('nb-NO', {
          style: 'currency', currency, maximumFractionDigits: 2,
        }).format(price);
        line.textContent = `${rate.name}: ${formatted}`;
        list.appendChild(line);
      }
      this.result.appendChild(list);
    } catch (error) {
      if (this.isConnected && this.input.value.trim() === postcode) {
        this.result.textContent = this.dataset.error;
      }
    } finally {
      clearTimeout(timeout);
      if (this.isConnected) {
        this.button.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
  }
}
if (!customElements.get('geethas-shipping-estimate')) {
  customElements.define('geethas-shipping-estimate', GeethasShippingEstimate);
}
